/**
 * Story #6490 (Bug #4838) 8.1 / 8.2 — one place that turns a key row's deletion
 * state into the words the customer sees. Used by the inventor and organisation
 * key pages, so the two pages can never describe the same state differently.
 *
 * in_progress / owed are their OWN state ("deleting…"), never "revocation
 * incomplete"; nothing_attributed and scope_not_owned are amber; exhausted is red,
 * and "support has been alerted" appears only once the alert was really sent
 * (purge_alert_sent_at) — until then "support is being alerted".
 */
export const NOTHING_ATTRIBUTED_TEXT =
  "No projects are attributed to this organisation, so nothing was deleted and search is NOT paused. Members' personal-workspace projects remain governed by each member's own key."
export const SCOPE_NOT_OWNED_TEXT = 'Nothing deleted — the scope is not in your workspace.'

export function exhaustedText(alertSentAt?: string | null): string {
  const alerted = alertSentAt
    ? 'SecureEmbed support has been alerted and will contact your administrator.'
    : 'SecureEmbed support is being alerted.'
  return `Deletion could not be verified complete after repeated attempts. ${alerted} Search stays paused.`
}

type Tone = 'info' | 'success' | 'amber' | 'red'

export function purgeStateView(state?: string | null, alertSentAt?: string | null): { tone: Tone; text: string } | null {
  switch (state) {
    case 'in_progress':
    case 'owed':
      return { tone: 'info', text: 'Deleting search entries and derived vectors — completes within about six minutes (up to about fifteen if the deletion was interrupted).' }
    case 'completed':
      return { tone: 'success', text: 'Search entries and derived vectors deleted. Backups holding earlier copies age out within 14 days.' }
    case 'nothing_attributed':
      return { tone: 'amber', text: NOTHING_ATTRIBUTED_TEXT }
    case 'scope_not_owned':
      return { tone: 'amber', text: SCOPE_NOT_OWNED_TEXT }
    case 'exhausted':
      return { tone: 'red', text: exhaustedText(alertSentAt) }
    case 'incomplete':
    case 'failed':
      return { tone: 'amber', text: 'Deletion is not yet verified complete; it is retried automatically.' }
    default:
      return null
  }
}

const TONE_CLASS: Record<Tone, string> = {
  info: 'bg-iflow-50 border-iflow-200 text-iflow-900',
  success: 'bg-green-50 border-green-200 text-green-800',
  amber: 'bg-amber-50 border-amber-200 text-amber-900',
  red: 'bg-red-50 border-red-200 text-red-800',
}

export default function PurgeStateNotice({ state, alertSentAt, testId }: {
  state?: string | null; alertSentAt?: string | null; testId?: string
}) {
  const view = purgeStateView(state, alertSentAt)
  if (!view) return null
  return (
    <p data-testid={testId ?? 'purge-state'} data-tone={view.tone}
       className={`text-sm border rounded-lg px-3 py-2 ${TONE_CLASS[view.tone]}`}>
      {view.text}
    </p>
  )
}
